import type { LinkNode } from 'components/link/types';
import { Tile, tileGridClassName } from 'components/Tile';
import React, { type MouseEvent, useCallback } from 'react';
import { useLocation } from 'wouter';

import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';

import { linkTypeToName } from './types';

const openLink = (
  event: MouseEvent,
  id: string,
  navigate: (path: string) => void
) => {
  const next = `/links/id/${encodeURIComponent(id)}`;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.button === 1) {
    const pathname = window.location.pathname.replace(/\/$/, '');
    const marker = '/integrations';
    const index = pathname.lastIndexOf(marker);
    const prefix =
      index === -1 ? pathname : pathname.slice(0, index + marker.length);
    window.open(`${window.location.origin}${prefix}${next}`, '_blank');
    return;
  }
  navigate(next);
};

const LinkTile = ({ link }: { link: LinkNode }) => {
  const [, navigate] = useLocation();
  const onClick = useCallback(
    (event: MouseEvent) => openLink(event, link.id, navigate),
    [link.id, navigate]
  );
  return (
    <Tile.Component onClick={onClick} data-testid={`${link.id}-link-tile`}>
      <div className="flex flex-row items-center gap-x-[1ch] text-main min-w-f0 w-full truncate">
        <LinkIcon type={link.__typename} />
        <span className="truncate">{link.name}</span>
      </div>
      <span className="hidden group-hover/tile:block text-main-500">
        {linkTypeToName[link.__typename]}
      </span>
    </Tile.Component>
  );
};

export const LinksGrid = ({ links }: { links: LinkNode[] }) => (
  <div className={tileGridClassName} data-testid="links-grid">
    {links.length > 0 ? (
      links.map((link) => <LinkTile key={link.id} link={link} />)
    ) : (
      <Tile.Component data-testid="no-links-tile">
        <span className="text-main-500">No Links Yet</span>
      </Tile.Component>
    )}
  </div>
);
